import React, { useEffect, useRef, useState, useContext } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import Card from 'react-bootstrap/Card'
import Spinner from 'react-bootstrap/Spinner'

import LoadingContext from '../../context/Loading/Loading'
import {
  getEventByShortCode,
  joinWaitingRoom,
  getWaitingRoomStatus,
  beaconLeaveWaitingRoom,
} from '../../utilities/api'
import {
  getPurchasePath,
  getWaitingRoomToken,
  setWaitingRoomDestination,
  setWaitingRoomToken,
} from '../../utilities/waitingRoom'

import './waitingRoomPage.scss'

const POLL_INTERVAL_MS = 6000

export default function WaitingRoomPage() {
  const { slug, shortcode } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { showLoading, hideLoading } = useContext(LoadingContext)

  const [event, setEvent] = useState(null)
  const [status, setStatus] = useState('loading')
  const [position, setPosition] = useState(null)
  const [queueSize, setQueueSize] = useState(null)
  const [estimatedWaitSeconds, setEstimatedWaitSeconds] = useState(null)
  const [error, setError] = useState(null)

  const pollRef = useRef(null)
  const eventUuidRef = useRef(null)
  const statusRef = useRef('loading')
  const skipLeaveRef = useRef(false)
  const destination = location.state?.destination || 'seated'

  // `soft` covers close-vs-refresh ambiguity: the seat is given up either way,
  // but the original queue position is held briefly so a reload keeps its place.
  const leaveQueueIfNeeded = ({ soft } = {}) => {
    if (skipLeaveRef.current) return
    // Only drop a waiting (or loading) seat. Admitted users keep their token
    // for the purchase flow; abandoned admissions expire via TTL.
    if (!['waiting', 'loading', 'not_joined'].includes(statusRef.current)) {
      return
    }
    const eventUuid = eventUuidRef.current
    if (!eventUuid) return
    beaconLeaveWaitingRoom(eventUuid, getWaitingRoomToken(eventUuid), { soft })
  }

  const redirectToPurchase = (eventUuid, token) => {
    skipLeaveRef.current = true
    statusRef.current = 'admitted'
    if (token && eventUuid) {
      setWaitingRoomToken(eventUuid, token)
    }
    const path = getPurchasePath(slug, shortcode, destination, eventUuid)
    navigate(path, { replace: true })
  }

  const handleQueueResponse = (data, eventUuid) => {
    if (data?.status === 'not_required') {
      redirectToPurchase(eventUuid)
      return
    }

    if (data?.status === 'admitted') {
      setStatus('admitted')
      redirectToPurchase(eventUuid, data.token)
      return
    }

    if (data?.status === 'waiting' || data?.status === 'not_joined') {
      statusRef.current = 'waiting'
      setStatus('waiting')
      setPosition(data.position)
      setQueueSize(data.queueSize)
      setEstimatedWaitSeconds(data.estimatedWaitSeconds)
    }
  }

  const pollStatus = async (eventUuid) => {
    try {
      const res = await getWaitingRoomStatus(eventUuid)
      handleQueueResponse(res.data, eventUuid)
    } catch (err) {
      console.error(err)
      setError('Unable to refresh queue status. Retrying...')
    }
  }

  useEffect(() => {
    showLoading()
    const code = 0
    skipLeaveRef.current = false
    statusRef.current = 'loading'

    getEventByShortCode(shortcode, slug, code)
      .then(async (res) => {
        const loadedEvent = res.data?.event
        if (!loadedEvent || res.data?.status === 404) {
          setError('Event not found.')
          statusRef.current = 'error'
          setStatus('error')
          return
        }

        setEvent(loadedEvent)
        eventUuidRef.current = loadedEvent.uuid

        if (!loadedEvent.waitingRoomEnabled) {
          redirectToPurchase(loadedEvent.uuid)
          return
        }

        setWaitingRoomDestination(loadedEvent.uuid, destination)

        const joinRes = await joinWaitingRoom(loadedEvent.uuid)
        handleQueueResponse(joinRes.data, loadedEvent.uuid)

        pollRef.current = setInterval(() => {
          pollStatus(loadedEvent.uuid)
        }, POLL_INTERVAL_MS)
      })
      .catch((err) => {
        console.error(err)
        setError('Something went wrong loading the waiting room.')
        statusRef.current = 'error'
        setStatus('error')
      })
      .finally(() => {
        hideLoading()
      })

    const onPageHide = (event) => {
      if (event?.persisted) return
      leaveQueueIfNeeded({ soft: true })
    }
    window.addEventListener('pagehide', onPageHide)

    return () => {
      window.removeEventListener('pagehide', onPageHide)
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
      // Navigating away inside the app is deliberate, so give the seat up fully.
      leaveQueueIfNeeded()
    }
  }, [shortcode, slug])

  const formatWaitTime = (seconds) => {
    if (!seconds || seconds <= 0) return 'less than a minute'
    if (seconds < 60) return `about ${seconds} seconds`
    const minutes = Math.ceil(seconds / 60)
    return `about ${minutes} minute${minutes === 1 ? '' : 's'}`
  }

  return (
    <div className="waiting-room-page full-height-wrapper">
      {event && (
        <Helmet>
          <title>{`Waiting Room — ${event.name}`}</title>
        </Helmet>
      )}

      <div className="waiting-room-page__container">
        <Card body className="waiting-room-page__card card-xl">
          {status === 'loading' && (
            <div className="waiting-room-page__loading text-center">
              <Spinner animation="border" role="status" />
              <p className="mt-3 mb-0">Joining the queue...</p>
            </div>
          )}

          {status === 'waiting' && (
            <>
              <p className="waiting-room-page__eyebrow">High demand event</p>
              <h1 className="waiting-room-page__title">
                {event?.name || 'You are in line'}
              </h1>
              <p className="waiting-room-page__subtitle">
                Please stay on this page. Closing or leaving this page removes
                you from the queue.
              </p>

              <div className="waiting-room-page__position">
                <span className="waiting-room-page__position-label">
                  Your position
                </span>
                <span className="waiting-room-page__position-value">
                  {position ?? '—'}
                </span>
              </div>

              {queueSize != null && (
                <p className="waiting-room-page__meta">
                  {queueSize} fans currently in the queue
                </p>
              )}

              {estimatedWaitSeconds != null && (
                <p className="waiting-room-page__meta">
                  Estimated wait: {formatWaitTime(estimatedWaitSeconds)}
                </p>
              )}

              <div className="waiting-room-page__progress">
                <div className="waiting-room-page__progress-bar" />
              </div>

              <p className="waiting-room-page__hint">
                You will be redirected automatically when it is your turn to
                shop.
              </p>
            </>
          )}

          {status === 'admitted' && (
            <div className="text-center">
              <Spinner animation="border" role="status" />
              <p className="mt-3 mb-0">You are in! Redirecting to tickets...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <p className="mb-0">
                {error || 'Unable to join the waiting room.'}
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
